if (!customElements.get('wls-filters-footer')) {
    class WLSStickyFooter extends HTMLElement {
      constructor() {
        super();
  
        this.classes = {
          sidebar: '.wls-filters',
          footer: '.wls-filters__footer',
          stopped: 'is-stopped',
        };
  
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
      }
  
      /** Scrollable ancestor (e.g. mobile filters drawer); window otherwise */
      getScrollRoots() {
        const roots = new Set([window]);
        let p = this.sidebar && this.sidebar.parentElement;
        while (p && p !== document.documentElement) {
          const st = getComputedStyle(p);
          if (
            (st.overflowY === 'auto' || st.overflowY === 'scroll') &&
            p.scrollHeight > p.clientHeight + 1
          ) {
            roots.add(p);
          }
          p = p.parentElement;
        }
        return roots;
      }
  
      connectedCallback() {
        this.init();
        this._attachScrollListeners();
        window.addEventListener('resize', this.onResize);
      }
  
      disconnectedCallback() {
        this._clearPendingStop();
        this._clearAwayDebounce();
        this._detachScrollListeners();
        window.removeEventListener('resize', this.onResize);
      }
  
      _attachScrollListeners() {
        this._detachScrollListeners();
        this._scrollRoots = this.getScrollRoots();
        this._scrollRoots.forEach((root) => {
          root.addEventListener('scroll', this.onScroll, { passive: true });
        });
      }
  
      _detachScrollListeners() {
        if (this._scrollRoots) {
          this._scrollRoots.forEach((root) => {
            root.removeEventListener('scroll', this.onScroll);
          });
          this._scrollRoots = null;
        }
      }
  
      init() {
        this.sidebar = this.closest(this.classes.sidebar);
        // Host is <wls-filters-footer class="wls-filters__footer"> — class is on self, not a child
        this.footerEl =
          this.matches(this.classes.footer) ? this : this.querySelector(this.classes.footer);
  
        if (!this.sidebar || !this.footerEl) return;
  
        this.update();
      }
  
      onScroll() {
        this.update();
      }
  
      onResize() {
        this._attachScrollListeners();
        this.update();
      }
  
      /** First scrollable ancestor of .wls-filters (drawer body, etc.) */
      _primaryScrollParent() {
        let p = this.sidebar && this.sidebar.parentElement;
        while (p && p !== document.documentElement) {
          const st = getComputedStyle(p);
          if (
            (st.overflowY === 'auto' || st.overflowY === 'scroll') &&
            p.scrollHeight > p.clientHeight + 1
          ) {
            return p;
          }
          p = p.parentElement;
        }
        return null;
      }

      /** Pixels remaining to reach scroll bottom (0 = flush). Optional cached scroll parent. */
      _scrollBottomGap(scrollParent) {
        const sp = scrollParent !== undefined ? scrollParent : this._primaryScrollParent();
        if (sp) {
          return sp.scrollHeight - sp.clientHeight - sp.scrollTop;
        }
        const el = document.documentElement;
        return el.scrollHeight - window.innerHeight - window.scrollY;
      }

      /**
       * Remove path: rect of .wls-filters vs viewport.
       * Add path: scroll gap (reliable “at bottom”) + rect fallback when no scroll parent.
       */
      _edgeMetrics() {
        const rect = this.sidebar.getBoundingClientRect();
        const vh = window.innerHeight;
        const awayLine = vh + WLSStickyFooter.AWAY_PX;
        const scrollParent = this._primaryScrollParent();
        const hasScrollParent = scrollParent != null;
        const gap = this._scrollBottomGap(scrollParent);

        const atEndZoneRect = rect.bottom <= vh + WLSStickyFooter.EDGE_IN_PX;
        /** True scroll bottom (or deep rect when layout has no overflow parent): instant add */
        const atScrollEndInstant =
          gap <= WLSStickyFooter.ENTER_INSTANT_GAP_PX ||
          (!hasScrollParent &&
            rect.bottom <= vh - WLSStickyFooter.ENTER_IMMEDIATE_RECT_PX);
        /** Near bottom: delayed add only if not instant */
        const atScrollEndFuzzy =
          gap <= WLSStickyFooter.ENTER_FUZZY_GAP_PX ||
          (!hasScrollParent && atEndZoneRect);

        return {
          atEndZone: atEndZoneRect,
          atScrollEndInstant,
          atScrollEndFuzzy,
          scrolledWellAway: rect.bottom > awayLine,
          scrolledFarAway: rect.bottom > awayLine + WLSStickyFooter.AWAY_IMMEDIATE_EXTRA_PX,
        };
      }

      _clearPendingStop() {
        if (this._stopTimerId != null) {
          clearTimeout(this._stopTimerId);
          this._stopTimerId = null;
        }
      }

      _clearAwayDebounce() {
        this._awaySince = null;
        if (this._awayRaf != null) {
          cancelAnimationFrame(this._awayRaf);
          this._awayRaf = null;
        }
      }

      /**
       * Add is-stopped: immediately at true scroll bottom (ENTER_INSTANT_GAP_PX) or deep rect
       * fallback; else after ENTER_DELAY_MS in fuzzy band only.
       * Remove: scrolledFarAway instant, else AWAY_HOLD_MS when scrolledWellAway.
       */
      update() {
        if (!this.sidebar || !this.footerEl) return;

        const { atScrollEndInstant, atScrollEndFuzzy, scrolledWellAway, scrolledFarAway } =
          this._edgeMetrics();
        const stopped = this.footerEl.classList.contains(this.classes.stopped);

        if (stopped) {
          this._clearPendingStop();

          if (scrolledFarAway) {
            this._clearAwayDebounce();
            this.footerEl.classList.remove(this.classes.stopped);
            return;
          }

          if (scrolledWellAway) {
            if (this._awaySince == null) {
              this._awaySince = performance.now();
            }
            if (this._awayRaf == null) {
              const tick = () => {
                this._awayRaf = null;
                if (!this.sidebar || !this.footerEl) return;
                if (!this.footerEl.classList.contains(this.classes.stopped)) return;
                const m = this._edgeMetrics();
                if (m.scrolledFarAway) {
                  this.footerEl.classList.remove(this.classes.stopped);
                  this._awaySince = null;
                  return;
                }
                if (!m.scrolledWellAway) {
                  this._awaySince = null;
                  return;
                }
                if (performance.now() - this._awaySince >= WLSStickyFooter.AWAY_HOLD_MS) {
                  this.footerEl.classList.remove(this.classes.stopped);
                  this._awaySince = null;
                  return;
                }
                this._awayRaf = requestAnimationFrame(tick);
              };
              this._awayRaf = requestAnimationFrame(tick);
            }
          } else {
            this._clearAwayDebounce();
          }
          return;
        }

        this._clearAwayDebounce();

        if (!atScrollEndFuzzy) {
          this._clearPendingStop();
          return;
        }

        if (atScrollEndInstant) {
          this._clearPendingStop();
          this.footerEl.classList.add(this.classes.stopped);
          return;
        }

        if (this._stopTimerId == null) {
          this._stopTimerId = setTimeout(() => {
            this._stopTimerId = null;
            if (!this.sidebar || !this.footerEl) return;
            const m = this._edgeMetrics();
            if (m.atScrollEndFuzzy) {
              this.footerEl.classList.add(this.classes.stopped);
            }
          }, WLSStickyFooter.ENTER_DELAY_MS);
        }
      }
    }
  
    /** px: "at bottom" band (start apply timer) */
    WLSStickyFooter.EDGE_IN_PX = 8;
    /** px: must scroll past this before we ever remove is-stopped (timed path) */
    WLSStickyFooter.AWAY_PX = 72;
    /** px: past AWAY_PX by this much → remove is-stopped immediately */
    WLSStickyFooter.AWAY_IMMEDIATE_EXTRA_PX = 0;
    /** ms: stay in scrolledWellAway this long before removing (when not past immediate threshold) */
    WLSStickyFooter.AWAY_HOLD_MS = 180;
    /** px: scroll gap to bottom ≤ this → add is-stopped immediately (no timer) */
    WLSStickyFooter.ENTER_INSTANT_GAP_PX = 100;
    /** px: scroll gap to bottom ≤ this → near-bottom band; uses timer only when gap > INSTANT */
    WLSStickyFooter.ENTER_FUZZY_GAP_PX = 100;
    /**
     * px: no scroll parent only — rect.bottom ≤ vh − this → instant add (mirror of “100px past” idea)
     */
    WLSStickyFooter.ENTER_IMMEDIATE_RECT_PX = 100;
    /** ms: in fuzzy scroll band before adding is-stopped */
    WLSStickyFooter.ENTER_DELAY_MS = 300;

    customElements.define('wls-filters-footer', WLSStickyFooter);
  }
  